import React, { useEffect, useState } from "react";
import { FiClock, FiPlus, FiTrash2 } from "react-icons/fi";
import { CopyUrlButton } from "../../components/common/CopyUrlButton";
import PrimaryButton from "../../components/common/PrimaryButton";
import SecondaryButton from "../../components/common/SecondaryButton";
import { getProvider } from "./Providers";
import { resolveExternalMeetingId } from "./meetingIdUtils";
import CalendarService, { buildAddCalendarEventPayload } from "../../services/CalendarService";
import "../../assets/css/VideoConference.css";
import "../../assets/css/Custom.css";

export default function MeetingSection({ providerKey }) {
  const provider = getProvider(providerKey);

  const [topic, setTopic] = useState("");
  const [isInstant, setIsInstant] = useState(true);
  const [startTime, setStartTime] = useState("");
  const [duration, setDuration] = useState(30);
  const [loading, setLoading] = useState(false);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchMeetings = async () => {
      try {
        const data = await provider.getUpcomingMeetings();
        if (isMounted) setUpcomingMeetings(data || []);
      } catch {
        if (isMounted) setUpcomingMeetings([]);
      }
    };

    fetchMeetings();
    const interval = setInterval(fetchMeetings, 10000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [providerKey, provider]);

  const canJoin = (startTime) => {
    if (!startTime) return true;
    const start = new Date(startTime);
    return currentTime >= new Date(start.getTime() - 5 * 60 * 1000);
  };

  const createMeeting = async () => {
    try {
      setLoading(true);

      const payload = {
        topic,
        isInstant,
        startTime: isInstant ? null : new Date(startTime).toISOString(),
        duration: isInstant ? null : duration,
      };

      const meeting = await provider.createMeeting(payload);

      const meetingUrl = meeting?.joinUrl || meeting?.meetingUrl || null;
      const meetingId = resolveExternalMeetingId(meeting, providerKey);
      try {
        const calendarPayload = await buildAddCalendarEventPayload({
          title: topic.trim(),
          startTime: isInstant ? new Date().toISOString() : startTime,
          durationInMinutes: isInstant ? (meeting?.duration || 30) : duration,
          isVCEnabled: true,
          meetingProvider: provider.getName(),
          meetingUrl,
          meetingId,
        });
        const resp = await CalendarService.addCalendarEvent(calendarPayload);
        if (resp?.data?.success === false) {
          console.warn('Calendar sync failed:', resp?.data?.message);
        }
      } catch (calendarErr) {
        console.error('Failed to sync meeting to calendar', calendarErr);
      }

      if (isInstant && meeting.joinUrl) {
        window.open(meeting.joinUrl, "_blank");
      } else {
        setUpcomingMeetings((prev) => [...prev, meeting]);
      }

      // reset form
      setTopic("");
      setStartTime("");
      setDuration(30);
      setIsInstant(true);
    } catch (err) {
      console.error(err);
      alert("Failed to create meeting. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  const deleteMeeting = async (meetingId) => {
    if (!window.confirm("Are you sure you want to delete this meeting?")) return;

    try {
      const token = await provider.getValidAccessToken();
      if (!token) throw new Error("Provider not authenticated");

      await provider.deleteMeeting(meetingId, token);
      setUpcomingMeetings(prev => prev.filter(m => m.id !== meetingId));
    } catch {
      alert("Failed to delete meeting");
    }
  };

  return (
    <div className="video-meeting-grid">
      {/* CREATE MEETING */}
      <div className="zoom-card">
        <h3 className="card-title">
          <FiPlus className="card-title-icon" /> Create Meeting
        </h3>

        <label>Meeting Topic</label>
        <input
          type="text"
          placeholder="Enter meeting topic"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
        />

        <label>Meeting Type</label>
        <div className="radio-group">
          <label>
            <input
              type="radio"
              checked={isInstant}
              onChange={() => setIsInstant(true)}
            />
            Instant
          </label>
          <label>
            <input
              type="radio"
              checked={!isInstant}
              onChange={() => setIsInstant(false)}
            />
            Scheduled
          </label>
        </div>

        {!isInstant && (
          <>
            <label>Start Time</label>
            <input
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />

            <label>Duration (minutes)</label>
            <input
              type="number"
              value={duration}
              min={1}
              onChange={(e) => setDuration(Number(e.target.value))}
            />
          </>
        )}

        <PrimaryButton
          className="start-meeting-btn"
          disabled={loading || !topic}
          onClick={createMeeting}
        >
          {isInstant ? "Start Meeting" : "Schedule Meeting"}
        </PrimaryButton>
      </div>

      {/* UPCOMING MEETINGS */}
      <div className="zoom-card">
        <div className="card-title-row">
          <h3 className="card-title">
            <FiClock className="card-title-icon" /> Upcoming Meetings
          </h3>
          {upcomingMeetings.length > 0 && (
            <span className="video-tab-badge video-tab-badge-static">{upcomingMeetings.length}</span>
          )}
        </div>

        {upcomingMeetings.length === 0 ? (
          <p className="empty-text">No upcoming meetings</p>
        ) : (
          <table className="meetings-table">
            <thead>
              <tr>
                <th>Topic</th>
                <th>Platform</th>
                <th>Start Time</th>
                <th>Duration</th>
                <th>Meeting URL</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {upcomingMeetings.map((m) => {
                const canUserJoin = canJoin(m.startTime);

                return (
                  <tr key={m.id}>
                    <td>{m.topic}</td>
                    <td>{provider.getName()}</td>
                    <td>{m.startTime ? new Date(m.startTime).toLocaleString() : "-"}</td>
                    <td>{m.duration || "-"}</td>
                    <td>{m.joinUrl && <CopyUrlButton url={m.joinUrl} />}</td>
                    <td>
                      <div className="join-btn-wrapper">
                        <button
                          className={`join-btn ${!canUserJoin ? "disabled" : ""}`}
                          disabled={!canUserJoin || !m.joinUrl}
                          onClick={() => window.open(m.joinUrl, "_blank")}
                        >
                          Join
                        </button>
                        {!canUserJoin && <span className="tooltip">Available 5 min before</span>}
                      </div>

                      <SecondaryButton
                        className="delete-btn"
                        onClick={() => deleteMeeting(m.id)}
                        title="Delete meeting"
                      >
                        <FiTrash2 size={13} /> Delete
                      </SecondaryButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
